import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { shopInfo } from '../../mock';
import { useToast } from '../../hooks/use-toast';
import { waitlistAPI } from '../../services/api';

export const ContactSection = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    partySize: '1',
    barber: 'First Available',
    service: 'Classic Haircut',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await waitlistAPI.join({
        name: formData.name,
        phone: formData.phone,
        party_size: parseInt(formData.partySize, 10),
        barber: formData.barber,
        service: formData.service,
        message: formData.message,
      });

      toast({
        title: "You're on the list!",
        description: "Taking you to your live status page...",
      });

      // Redirect to their personal status page
      setTimeout(() => {
        navigate(`/my-status/${result.id}`);
      }, 600);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const isCapacityError = error.response?.status === 400 && detail;
      toast({
        title: isCapacityError ? detail : "Something went wrong",
        description: isCapacityError
          ? "Please try again tomorrow or call us at (662) 323-4205."
          : (detail || "Please try again or call us directly."),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-stone-900 mb-4">
            Join Our Waitlist
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Add yourself to the waitlist and we'll serve you in order of arrival
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-stone-900 mb-6">Get in Touch</h3>
              <p className="text-stone-600 leading-relaxed mb-8">
                Ready for a fresh cut? Add yourself to our online waitlist and we'll serve you in the order you arrive. 
                Walk-ins are always welcome!
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4 p-4 bg-white rounded-lg border border-stone-200 hover:border-maroon-700 transition-colors duration-200">
                <div className="bg-maroon-100 p-3 rounded-lg flex-shrink-0">
                  <Phone className="h-6 w-6 text-maroon-800" />
                </div>
                <div>
                  <p className="text-sm text-stone-600 mb-1">Phone</p>
                  <a
                    href={`tel:${shopInfo.phone}`}
                    className="text-lg font-semibold text-stone-900 hover:text-maroon-800 transition-colors duration-200"
                  >
                    {shopInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white rounded-lg border border-stone-200 hover:border-maroon-700 transition-colors duration-200">
                <div className="bg-maroon-100 p-3 rounded-lg flex-shrink-0">
                  <MapPin className="h-6 w-6 text-maroon-800" />
                </div>
                <div>
                  <p className="text-sm text-stone-600 mb-1">Address</p>
                  <p className="text-lg font-semibold text-stone-900">
                    {shopInfo.address.street}
                  </p>
                  <p className="text-stone-700">
                    {shopInfo.address.city}, {shopInfo.address.state} {shopInfo.address.zip}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white rounded-lg border border-stone-200 hover:border-maroon-700 transition-colors duration-200">
                <div className="bg-maroon-100 p-3 rounded-lg flex-shrink-0">
                  <Mail className="h-6 w-6 text-maroon-800" />
                </div>
                <div>
                  <p className="text-sm text-stone-600 mb-1">Walk-ins</p>
                  <p className="text-lg font-semibold text-stone-900">
                    Always Welcome!
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-maroon-800 p-6 rounded-xl text-stone-100">
              <h4 className="font-bold text-lg mb-2">Walk-ins Welcome</h4>
              <p className="text-rose-100">
                No waitlist needed! Stop by during our business hours and we'll get you taken care of.
              </p>
            </div>
          </div>

          {/* Waitlist Form */}
          <div className="bg-white rounded-2xl p-8 border-2 border-stone-200 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="waitlist-form">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-stone-900 mb-2">
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  data-testid="waitlist-name-input"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="border-stone-300 focus:border-maroon-700 focus:ring-maroon-700"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-stone-900 mb-2">
                  Phone Number *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  data-testid="waitlist-phone-input"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="border-stone-300 focus:border-maroon-700 focus:ring-maroon-700"
                />
              </div>

              <div>
                <label htmlFor="partySize" className="block text-sm font-semibold text-stone-900 mb-2">
                  Party Size *
                </label>
                <select
                  id="partySize"
                  name="partySize"
                  data-testid="waitlist-party-size-select"
                  required
                  value={formData.partySize}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-maroon-700 focus:border-maroon-700 bg-white"
                >
                  <option value="1">1 person</option>
                  <option value="2">2 people</option>
                  <option value="3">3 people</option>
                  <option value="4">4 people</option>
                  <option value="5">5+ people</option>
                </select>
              </div>

              <div>
                <label htmlFor="barber" className="block text-sm font-semibold text-stone-900 mb-2">
                  Preferred Barber *
                </label>
                <select
                  id="barber"
                  name="barber"
                  data-testid="waitlist-barber-select"
                  required
                  value={formData.barber}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-maroon-700 focus:border-maroon-700 bg-white"
                >
                  <option value="Pete">Pete</option>
                  <option value="Mckee">Mckee</option>
                  <option value="Chris">Chris</option>
                  <option value="First Available">First Available</option>
                </select>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-stone-900 mb-2">
                  Service *
                </label>
                <select
                  id="service"
                  name="service"
                  data-testid="waitlist-service-select"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-maroon-700 focus:border-maroon-700 bg-white"
                >
                  <option value="Classic Haircut">Classic Haircut (~20 min)</option>
                  <option value="Beard Trim">Beard Trim (~15 min)</option>
                  <option value="Haircut & Beard Combo">Haircut & Beard Combo (~30 min)</option>
                  <option value="Kids Haircut">Kids Haircut (~20 min)</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-stone-900 mb-2">
                  Note to the shop (optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  maxLength={500}
                  placeholder="e.g., Running 10 min late, bringing my son too, etc."
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-maroon-700 focus:border-maroon-700 bg-white resize-none"
                  data-testid="note-field"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="waitlist-submit-button"
                className="w-full bg-maroon-800 hover:bg-maroon-900 text-stone-100 py-6 text-lg font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span>Adding to Waitlist...</span>
                ) : (
                  <span className="flex items-center justify-center space-x-2">
                    <Send className="h-5 w-5" />
                    <span>Join Waitlist</span>
                  </span>
                )}
              </Button>

              <p className="text-sm text-stone-500 text-center">
                You'll be served in the order you arrive
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
